import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getAuthHeaders } from '../../utils/authUtils';
import { createApiUrl } from '../../utils/apiConfig';
import { useSnackbar } from '../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../utils/dateTimeFormat';

const ModalityStatistics = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const theme = useTheme();
  
  // Ref für den Donut Chart
  const chartRef = useRef(null);
  
  // State
  const [modalitiesData, setModalitiesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [hoveredModality, setHoveredModality] = useState(null);

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Helper: Get current period based on timeFilter
  const getCurrentPeriod = (filterType) => {
    const now = new Date();
    
    switch (filterType) {
      case 'date':
        return now.toISOString().split('T')[0];
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
        return weekStart.toISOString().split('T')[0];
      case 'month':
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `${now.getFullYear()}-Q${quarter}`;
      case 'year':
        return `${now.getFullYear()}`;
      default:
        return '';
    }
  };

  // Helper: Get next/previous period
  const getAdjacentPeriod = (period, filterType, direction) => {
    if (!period) return '';
    
    switch (filterType) {
      case 'date': {
        const date = new Date(period);
        date.setDate(date.getDate() + direction);
        return date.toISOString().split('T')[0];
      }
      case 'week': {
        const date = new Date(period);
        date.setDate(date.getDate() + (direction * 7));
        return date.toISOString().split('T')[0];
      }
      case 'month': {
        const [year, month] = period.split('-').map(Number);
        const date = new Date(year, month - 1 + direction, 1);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      case 'quarter': {
        const [year, q] = period.split('-');
        const quarter = parseInt(q.replace('Q', ''));
        const newQuarter = quarter + direction;
        if (newQuarter < 1) {
          return `${parseInt(year) - 1}-Q4`;
        } else if (newQuarter > 4) {
          return `${parseInt(year) + 1}-Q1`;
        }
        return `${year}-Q${newQuarter}`;
      }
      case 'year': {
        const year = parseInt(period);
        return `${year + direction}`;
      }
      default:
        return period;
    }
  };

  // Set default period on mount
  useEffect(() => {
    const currentPeriod = getCurrentPeriod(timeFilter);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  }, []);

  // Load data when filters change
  useEffect(() => {
    if (startPeriod && endPeriod) {
      loadModalitiesData();
    }
  }, [timeFilter, startPeriod, endPeriod]);

  // Wheel event for period scrolling (Shift + Scroll)
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey && (startPeriod || endPeriod)) {
        event.preventDefault();
        
        const direction = event.deltaY > 0 ? -1 : 1;
        
        setStartPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
        setEndPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter, startPeriod, endPeriod]);

  const loadModalitiesData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        interval: timeFilter,
        start_period: startPeriod,
        end_period: endPeriod
      });

      const response = await fetch(
        createApiUrl(`statistics/modality?${params.toString()}`),
        {
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setModalitiesData(data);
      } else {
        showError(t('statistics.modality.loadError', 'Fehler beim Laden der Modalitäten-Statistik'));
      }
    } catch (error) {
      console.error('Error loading modality statistics:', error);
      showError(t('statistics.modality.loadError', 'Fehler beim Laden der Modalitäten-Statistik'));
    } finally {
      setLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    const newFilterType = event.target.value;
    setTimeFilter(newFilterType);
    const currentPeriod = getCurrentPeriod(newFilterType);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  };

  const formatPeriod = (period, filterType) => {
    if (!period || !isFormatConfigLoaded) return period;
    
    switch (filterType) {
      case 'date':
        return formatDateSync(period);
      case 'week': {
        const date = new Date(period);
        const weekNum = getWeekNumber(date);
        return `${t('statistics.week', 'KW')} ${weekNum}, ${date.getFullYear()}`;
      }
      case 'month': {
        const [year, month] = period.split('-');
        const monthNames = [
          t('months.january', 'Januar'), t('months.february', 'Februar'),
          t('months.march', 'März'), t('months.april', 'April'),
          t('months.may', 'Mai'), t('months.june', 'Juni'),
          t('months.july', 'Juli'), t('months.august', 'August'),
          t('months.september', 'September'), t('months.october', 'Oktober'),
          t('months.november', 'November'), t('months.december', 'Dezember')
        ];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      }
      case 'quarter': {
        const [year, q] = period.split('-');
        return `${q}, ${year}`;
      }
      case 'year':
        return period;
      default:
        return period;
    }
  };

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  // Get available periods for dropdowns - Generate complete range
  const getAvailablePeriods = () => {
    const periods = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    
    switch (timeFilter) {
      case 'date': {
        // Generate last 365 days
        for (let i = 365; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          periods.push(date.toISOString().split('T')[0]);
        }
        break;
      }
      case 'week': {
        // Generate last 104 weeks (2 years)
        for (let i = 104; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - (i * 7));
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
          periods.push(weekStart.toISOString().split('T')[0]);
        }
        break;
      }
      case 'month': {
        // Generate months from 1999-01 to current month
        for (let year = 1999; year <= currentYear; year++) {
          const endMonth = year === currentYear ? now.getMonth() + 1 : 12;
          for (let month = 1; month <= endMonth; month++) {
            periods.push(`${year}-${String(month).padStart(2, '0')}`);
          }
        }
        break;
      }
      case 'quarter': {
        // Generate quarters from 1999-Q1 to current quarter
        const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
        for (let year = 1999; year <= currentYear; year++) {
          const endQuarter = year === currentYear ? currentQuarter : 4;
          for (let q = 1; q <= endQuarter; q++) {
            periods.push(`${year}-Q${q}`);
          }
        }
        break;
      }
      case 'year': {
        // Generate years from 1999 to current year
        for (let year = 1999; year <= currentYear; year++) {
          periods.push(`${year}`);
        }
        break;
      }
    }
    
    return periods;
  };

  // Get chart colors from CSS variables
  const getChartColors = (count) => {
    const baseColors = [
      'var(--rd-primary)', 'var(--rd-chart-2)', 'var(--rd-chart-3)', 'var(--rd-chart-4)', 'var(--rd-chart-5)',
      'var(--rd-chart-6)', 'var(--rd-chart-7)', 'var(--rd-chart-8)', 'var(--rd-chart-9)', 'var(--rd-chart-10)'
    ];
    
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    
    // Falls mehr als 10 Modalitäten: Generiere zusätzliche Farben
    const colors = [...baseColors];
    const goldenRatio = 0.618033988749895;
    let hue = Math.random();
    
    for (let i = baseColors.length; i < count; i++) {
      hue += goldenRatio;
      hue %= 1;
      const saturation = 0.5 + Math.random() * 0.3;
      const lightness = 0.5 + Math.random() * 0.2;
      colors.push(`hsl(${Math.floor(hue * 360)}, ${Math.floor(saturation * 100)}%, ${Math.floor(lightness * 100)}%)`);
    }
    
    return colors;
  };

  // Calculate total based on selection
  const calculateTotal = () => {
    if (!modalitiesData || !modalitiesData.modalities) return 0;
    
    const modalities = modalitiesData.modalities;
    
    if (selectedModalities.length === 0) {
      // No selection: return grand total
      return Object.values(modalities).reduce((sum, count) => sum + count, 0);
    } else {
      // Sum selected modalities
      return selectedModalities.reduce((sum, modality) => {
        return sum + (modalities[modality] || 0);
      }, 0);
    }
  };

  // Handle bar click for selection
  const handleBarClick = (event, d) => {
    if (!d || d.dataIndex === undefined) return;
    
    // Get the original modality name from the data
    const modalities = modalitiesData.modalities;
    const entries = Object.entries(modalities).sort((a, b) => b[1] - a[1]);
    const clickedModality = entries[d.dataIndex][0];
    
    setSelectedModalities(prev => {
      if (prev.includes(clickedModality)) {
        // Deselect
        return prev.filter(m => m !== clickedModality);
      } else {
        // Select
        return [...prev, clickedModality];
      }
    });
  };

  // Helper: Make color lighter/greyed out
  const makeColorFaded = (color) => {
    // Handle RGB format
    if (color.startsWith('rgb(')) {
      return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/, (match, r, g, b) => {
        const avg = Math.floor((parseInt(r) + parseInt(g) + parseInt(b)) / 3);
        const lighter = Math.floor(avg + (255 - avg) * 0.7);
        return `rgb(${lighter}, ${lighter}, ${lighter})`;
      });
    }
    
    // Handle HSL format
    if (color.startsWith('hsl(')) {
      return color.replace(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/, (match, h, s, l) => {
        // Convert to greyscale by setting saturation to 0 and increasing lightness
        return `hsl(0, 0%, 80%)`;
      });
    }
    
    // Handle HEX format
    if (color.startsWith('#')) {
      return color.replace(/#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i, (match, r, g, b) => {
        const rVal = parseInt(r, 16);
        const gVal = parseInt(g, 16);
        const bVal = parseInt(b, 16);
        const avg = Math.floor((rVal + gVal + bVal) / 3);
        const lighter = Math.floor(avg + (255 - avg) * 0.7);
        const hex = lighter.toString(16).padStart(2, '0');
        return `#${hex}${hex}${hex}`;
      });
    }
    
    // Fallback: return light grey
    return 'var(--rd-border)';
  };

  // Helper: Sanitize modality name for CSS class names (remove special characters like /)
  const sanitizeForCss = (name) => {
    if (!name) return 'unknown';
    // Replace special characters with hyphens to make CSS-safe
    return name.replace(/[^a-zA-Z0-9-_]/g, '-');
  };

  // Prepare chart data for BarChart
  const getChartData = () => {
    if (!modalitiesData || !modalitiesData.modalities) return { labels: [], series: [], colors: [], entries: [], labelMapping: {} };
    
    const modalities = modalitiesData.modalities;
    const entries = Object.entries(modalities).sort((a, b) => b[1] - a[1]); // Sort by count descending
    
    // Create mapping: sanitized -> original for display
    const labelMapping = {};
    entries.forEach(([modality]) => {
      const sanitized = sanitizeForCss(modality) || 'unknown';
      labelMapping[sanitized] = modality || t('statistics.modality.unknown', 'Unbekannt');
    });
    
    // Sanitize labels for CSS class names (PT/CT -> PT-CT)
    const labels = entries.map(([modality]) => sanitizeForCss(modality) || 'unknown');
    const baseColors = getChartColors(entries.length);
    
    // Create one series per modality so each bar can have a different color
    const series = entries.map(([modality, count], index) => {
      // Check selection and hover
      const isSelected = selectedModalities.includes(modality);
      const hasSelection = selectedModalities.length > 0;
      const isHovered = hoveredModality === modality;
      const hasHover = hoveredModality !== null;
      
      // Determine color based on hover and selection
      let color = baseColors[index];
      if (hasHover && !isHovered) {
        // When hovering, fade everything except the hovered item
        color = makeColorFaded(baseColors[index]);
      } else if (hasSelection && !isSelected && !hasHover) {
        // When selected (and not hovering), fade non-selected
        color = makeColorFaded(baseColors[index]);
      }
      
      return {
        data: entries.map((_, i) => i === index ? count : 0),
        // Sanitize label for CSS class names (PT/CT -> PT-CT)
        label: sanitizeForCss(modality) || 'unknown',
        // Keep original modality name for display
        id: modality || t('statistics.modality.unknown', 'Unbekannt'),
        stack: 'total',
        color: color
      };
    });
    
    return { labels, series, colors: baseColors, entries, labelMapping };
  };

  // PDF Export
  const handlePdfDownload = async () => {
    if (!modalitiesData || !chartRef.current) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(16);
    doc.text(t('statistics.modality.pdfTitle', 'Modalitäten-Statistik'), 14, 20);
    
    // Period info
    doc.setFontSize(10);
    const periodText = `${t('statistics.period', 'Zeitraum')}: ${formatPeriod(startPeriod, timeFilter)} - ${formatPeriod(endPeriod, timeFilter)}`;
    doc.text(periodText, 14, 30);
    
    // Total studies info
    if (modalitiesData.total_studies) {
      doc.text(`${t('statistics.modality.totalStudies', 'Gesamtanzahl Studien')}: ${modalitiesData.total_studies}`, 14, 36);
    }
    
    // Add chart as image
    try {
      const chartElement = chartRef.current;
      const canvas = await html2canvas(chartElement);
      const imgData = canvas.toDataURL('image/png');
      doc.addImage(imgData, 'PNG', 14, 45, pageWidth - 28, 100);
    } catch (error) {
      console.error('Error capturing chart:', error);
    }
    
    // Add table with data
    const chartData = getChartData();
    const tableData = chartData.labels.map((label, idx) => {
      const count = chartData.series[idx].data.find(val => val > 0) || 0;
      // Use original modality name from mapping (PT/CT instead of PT-CT)
      const originalLabel = chartData.labelMapping[label] || label;
      return [originalLabel, count];
    });
    
    doc.autoTable({
      startY: 155,
      head: [[
        t('statistics.modality.modalityColumn', 'Modalität'),
        t('statistics.modality.countColumn', 'Anzahl Studien')
      ]],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [248, 148, 0] }
    });
    
    // Footer
    const user = currentUser;
    const userName = `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim();
    const now = new Date();
    const footer = `${t('statistics.modality.pdfTitle', 'Modalitäten-Statistik')}, ${t('statistics.createdBy', 'erstellt von')} ${userName}, ${t('statistics.on', 'am')} ${formatDateSync(now.toISOString())}`;
    
    const footerY = doc.internal.pageSize.getHeight() - 10;
    doc.setFontSize(8);
    doc.text(footer, 14, footerY);
    
    doc.save(`modality-report.pdf`);
  };

  // CSV Export
  const handleCsvDownload = () => {
    if (!modalitiesData) return;
    
    const chartData = getChartData();
    
    // CSV Header
    let csv = `${t('statistics.modality.modalityColumn', 'Modalität')};${t('statistics.modality.countColumn', 'Anzahl Studien')}\n`;
    
    // CSV Data - Extract count from each series
    chartData.series.forEach((series, idx) => {
      const count = series.data.find(val => val > 0) || 0;
      // Use original modality name from mapping (PT/CT instead of PT-CT)
      const originalLabel = chartData.labelMapping[chartData.labels[idx]] || chartData.labels[idx];
      csv += `${originalLabel};${count}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `modality-report.csv`;
    link.click();
  };

  const chartData = getChartData();
  const availablePeriods = getAvailablePeriods();

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        {t('statistics.modality.title', 'Modalitäten-Statistik')}
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Zeitintervall */}
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{t('statistics.interval', 'Zeitraum')}</InputLabel>
              <Select
                value={timeFilter}
                onChange={handleTimeFilterChange}
                label={t('statistics.interval', 'Zeitraum')}
              >
                <MenuItem value="date">{t('statistics.intervals.date', 'Tag')}</MenuItem>
                <MenuItem value="week">{t('statistics.intervals.week', 'Woche')}</MenuItem>
                <MenuItem value="month">{t('statistics.intervals.month', 'Monat')}</MenuItem>
                <MenuItem value="quarter">{t('statistics.intervals.quarter', 'Quartal')}</MenuItem>
                <MenuItem value="year">{t('statistics.intervals.year', 'Jahr')}</MenuItem>
              </Select>
            </FormControl>

            {/* Von */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('statistics.from', 'Von')}</InputLabel>
              <Select
                value={startPeriod}
                onChange={(e) => setStartPeriod(e.target.value)}
                label={t('statistics.from', 'Von')}
              >
                {availablePeriods.map((period) => (
                  <MenuItem key={period} value={period}>
                    {formatPeriod(period, timeFilter)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Bis */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('statistics.to', 'Bis')}</InputLabel>
              <Select
                value={endPeriod}
                onChange={(e) => setEndPeriod(e.target.value)}
                label={t('statistics.to', 'Bis')}
              >
                {availablePeriods.map((period) => (
                  <MenuItem key={period} value={period}>
                    {formatPeriod(period, timeFilter)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Total Display */}
          <Box sx={{ textAlign: 'right', minWidth: '150px' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {selectedModalities.length === 0 
                ? t('statistics.modality.grandTotal', 'Gesamt') 
                : t('statistics.modality.selectedTotal', 'Auswahl')}
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {calculateTotal().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Chart */}
      {!loading && chartData.labels.length > 0 && (
        <Paper sx={{ p: 3 }} ref={chartRef}>
          <Typography variant="h6" gutterBottom align="center">
            {t('statistics.modality.chartTitle', 'Studien nach Modalität')}
          </Typography>
          <BarChart
            xAxis={[{
              scaleType: 'band',
              data: chartData.labels,
              hideTooltip: true,
              // Display original modality names (PT/CT instead of PT-CT)
              valueFormatter: (value) => chartData.labelMapping[value] || value
            }]}
            yAxis={[{
              label: t('statistics.modality.countColumn', 'Anzahl Studien')
            }]}
            series={chartData.series}
            height={400}
            margin={{ top: 20, right: 30, bottom: 20, left: 80 }}
            onItemClick={(event, params) => handleBarClick(event, params)}
            onAxisClick={(event, params) => {
              // Prevent default tooltip
              event.preventDefault();
            }}
            slotProps={{
              legend: { hidden: true },
              popper: {
                sx: {
                  '& .MuiChartsTooltip-root': {
                    display: 'none !important'
                  }
                }
              }
            }}
            tooltip={{ trigger: 'none' }}
            sx={{
              '& .MuiChartsLegend-root': {
                display: 'none !important'
              },
              '& .MuiChartsTooltip-root': {
                display: 'none !important'
              },
              '& .MuiBarElement-root': {
                cursor: 'pointer'
              },
              '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                display: 'none'
              },
              '& .MuiChartsAxis-bottom .MuiChartsAxis-label': {
                display: 'none'
              }
            }}
          />
          
          {/* Custom Legend */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {chartData.entries.map(([modality, count], index) => {
              const isSelected = selectedModalities.includes(modality);
              const isHovered = hoveredModality === modality;
              
              return (
                <Box
                  key={modality}
                  onClick={() => handleBarClick(null, { dataIndex: index })}
                  onMouseEnter={() => setHoveredModality(modality)}
                  onMouseLeave={() => setHoveredModality(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '4px 8px',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isHovered ? 'action.hover' : 'transparent',
                    border: isSelected ? '2px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: chartData.colors[index],
                      borderRadius: 0.5
                    }}
                  />
                  <Typography variant="body2">
                    {modality || t('statistics.modality.unknown', 'Unbekannt')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({count})
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* No Data */}
      {!loading && chartData.labels.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('statistics.noData', 'Keine Daten verfügbar')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
});

ModalityStatistics.displayName = 'ModalityStatistics';

export default ModalityStatistics;
